import { useEffect, useRef, useState } from 'preact/hooks';
import { useSearchParams } from 'react-router-dom';
import { CheckIcon, CopyIcon, MailIcon, PenLineIcon, ShieldCheckIcon } from 'lucide-react';
import { AppShell } from '@/flows/marketplace/components/app-shell.js';
import { Card, CardIntro } from '@/flows/marketplace/components/card.js';

export function RedirectSuccess() {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id');
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (copiedTimer.current !== null) window.clearTimeout(copiedTimer.current);
    },
    [],
  );

  async function handleCopy() {
    if (orderId === null) return;
    try {
      await navigator.clipboard.writeText(orderId);
      setCopied(true);
      if (copiedTimer.current !== null) window.clearTimeout(copiedTimer.current);
      copiedTimer.current = window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API can fail (insecure context, denied permission).
    }
  }

  return (
    <AppShell faq={false}>
      <Card className="sm-card-centered">
        <div className="sm-center sm-badge" data-tone="success">
          <CheckIcon size={30} strokeWidth={3} aria-hidden="true" />
        </div>

        <CardIntro
          title="Payment confirmed"
          subtitle="Thanks for your purchase. Look for an email from DocuSign in the next few minutes to sign your contracts."
        />

        <div className="sm-stack sm-next">
          <p className="sm-type-eyebrow">What happens next</p>
          <ul className="sm-stack sm-next-list">
            <NextItem
              icon={MailIcon}
              title="Check your email"
              body="DocuSign will send your contracts to the email you provided. Usually within a few minutes."
            />
            <NextItem
              icon={PenLineIcon}
              title="Sign your contracts"
              body="Open the DocuSign email and follow the prompts to sign — takes about a minute."
            />
            <NextItem
              icon={ShieldCheckIcon}
              title="Coverage begins"
              body="Your coverage activates as soon as the contracts are signed."
            />
          </ul>
        </div>

        {orderId !== null && (
          <div className="sm-stack sm-ref">
            <span className="sm-type-eyebrow">Order reference</span>
            <span className="sm-type-body sm-ref-value-row">
              <span>{orderId}</span>
              <button
                type="button"
                className="sm-center sm-ref-copy"
                aria-label={copied ? 'Copied' : 'Copy order reference'}
                onClick={handleCopy}
              >
                {copied ? (
                  <CheckIcon size={12} strokeWidth={3} aria-hidden="true" />
                ) : (
                  <CopyIcon size={12} aria-hidden="true" />
                )}
              </button>
            </span>
            <p className="sm-type-caption">
              Save this for support — they&apos;ll ask for it if you need help.
            </p>
          </div>
        )}
      </Card>
    </AppShell>
  );
}

interface NextItemProps {
  icon: typeof MailIcon;
  title: string;
  body: string;
}

function NextItem({ icon: Icon, title, body }: NextItemProps) {
  return (
    <li className="sm-next-item">
      <span className="sm-center sm-next-icon">
        <Icon size={14} aria-hidden="true" />
      </span>
      <div className="sm-stack sm-next-text">
        <span className="sm-type-body-strong">{title}</span>
        <span className="sm-type-body sm-next-text-body">{body}</span>
      </div>
    </li>
  );
}
